import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Lenis from "lenis";

import "@/App.css";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import SiteLoader from "./components/SiteLoader";
import CustomCursor from "./components/CustomCursor";
import FloatingActions from "./components/FloatingActions";
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import ServicesPage from "./pages/ServicesPage";
import ProjectsPage from "./pages/ProjectsPage";
import ClientsPage from "./pages/ClientsPage";
import ConsultationPage from "./pages/ConsultationPage";
import ContactPage from "./pages/ContactPage";

function LenisProvider({ children }) {
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: false,
    });
    lenisRef.current = lenis;
    window.__lenis = lenis;

    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    const onAnchorClick = (e) => {
      const anchor = e.target.closest('a[href^="#"]');
      if (!anchor) return;
      const id = anchor.getAttribute("href");
      if (!id || id === "#") return;
      const el = document.querySelector(id);
      if (!el) return;
      e.preventDefault();
      lenis.scrollTo(el, { offset: -90, duration: 1.4 });
    };
    document.addEventListener("click", onAnchorClick);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener("click", onAnchorClick);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return children;
}

function ScrollToTopOnRouteChange() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
}

import AdminLayout from "./pages/admin/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import ProjectsManager from "./pages/admin/ProjectsManager";
import GalleryManager from "./pages/admin/GalleryManager";
import LeadsManager from "./pages/admin/LeadsManager";
import PagesManager from "./pages/admin/PagesManager";
import VideosManager from "./pages/admin/VideosManager";
import SettingsManager from "./pages/admin/SettingsManager";
import LoginPage from "./pages/admin/LoginPage";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <div className="App relative">
        <SiteLoader />
        <div className="noise-overlay" />
        <CustomCursor />
        <LenisProvider>
          <BrowserRouter>
            <ScrollToTopOnRouteChange />
            <Routes>
              {/* Admin Auth Route */}
              <Route path="/admin/login" element={<LoginPage />} />

              {/* Admin Routes */}
              <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="projects" element={<ProjectsManager />} />
              <Route path="gallery" element={<GalleryManager />} />
              <Route path="leads" element={<LeadsManager />} />
              <Route path="pages" element={<PagesManager />} />
              <Route path="videos" element={<VideosManager />} />
              <Route path="settings" element={<SettingsManager />} />
            </Route>

            {/* Public Routes */}
            <Route path="*" element={
              <>
                <Navbar />
                <main>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/services" element={<ServicesPage />} />
                    <Route path="/projects/completed" element={<ProjectsPage status="completed" />} />
                    <Route path="/projects/ongoing" element={<ProjectsPage status="ongoing" />} />
                    <Route path="/clients" element={<ClientsPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="*" element={<HomePage />} />
                  </Routes>
                </main>
                <Footer />
                <FloatingActions />
              </>
            } />
          </Routes>
        </BrowserRouter>
      </LenisProvider>
    </div>
    </AuthProvider>
  );
}

export default App;
